// src/sections/GiftSetsSlider.jsx
import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { useDataRefresh } from "../liveUpdates/DataRefreshProvider";
import axios from "axios";
import { ShoppingCart } from "lucide-react";
import FavoriteButton from "../FavoriteButton"; // შეცვალე გზა თუ სხვაგან დევს
import { useLanguage } from "../LanguageContext";

const API_BASE_URL = "https://shopaholics.ge/api/gift_sets";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

const Section = styled.div`
  max-width: 1280px;
  margin: 10px auto;
  padding: 0px 16px 28px;

  @media (max-width: 640px) {
    padding-left: 0;
    padding-right: 0;
  }

  h3 {
    margin: 8px 0 14px;
    font-size: 20px;
    font-weight: 900;
    color: ${COLORS.text};
    padding: 0 4px;
  }
`;

const CarouselWrap = styled.div` position: relative; width: 100%; `;

const Track = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 8px 14px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 8px;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-thumb { background: #d9d9dc; border-radius: 8px; }

  @media (max-width: 640px) {
    gap: 12px;
    padding-left: 8px;
    padding-right: 8px;
  }
`;

const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 42px; width: 42px;
  border-radius: 50%;
  border: 1px solid ${COLORS.line};
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(6px);
  display: grid; place-items: center;
  box-shadow: ${COLORS.shadow};
  cursor: pointer; z-index: 2;
  transition: transform .15s ease, filter .15s ease, opacity .15s ease;

  &:hover { transform: translateY(-50%) scale(1.03); filter: brightness(.98); }
  &:disabled { opacity: .35; cursor: not-allowed; }

  @media (max-width: 640px) { height: 36px; width: 36px; }
`;
const ArrowLeft  = styled(ArrowBtn)` left: 6px; `;
const ArrowRight = styled(ArrowBtn)` right: 6px; `;

const Card = styled.div`
  scroll-snap-align: start;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  min-width: clamp(200px, 24vw, 260px);
  max-width: 280px;
  padding: 12px;
  position: relative;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    box-shadow: ${COLORS.shadow};
    transform: translateY(-2px);
    border-color: rgba(0,0,0,.12);
  }

  /* overlay მხოლოდ დაკლიკვაზე ($active) */
  .hover-options {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; z-index: 40;
    border-radius: 16px;
    pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  }

  .view-btn{
    border: 1px solid ${COLORS.line};
    background:#fff;
    border-radius: 12px;
    height: 40px; padding: 0 14px;
    font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease;
    color: black;
  }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  @media (max-width: 640px) {
    padding: 8px;
    border-radius: 12px;
    min-width: clamp(180px, 48vw, 220px);
  }
`;

/* ზუსტად ისე, როგორც ქვედა სლაიდერში */
const IconBar = styled.div`
  position: absolute;
  left: 10px;
  top: 10px;
  display: flex;
  gap: 8px;
  z-index: 120;

  .icon-btn {
    width: 36px; height: 36px;
    border-radius: 10px;
    border: 1px solid ${COLORS.line};
    background: #fff;
    display: grid; place-items: center;
    cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover { transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  @media (max-width: 640px) {
    .icon-btn { width: 32px; height: 32px; }
  }
`;

const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;

  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;

const ImgWrap = styled.div`
  width: 100%; aspect-ratio: 1 / 1;
  border-radius: 12px; background: ${COLORS.subtle};
  display: grid; place-items: center; overflow: hidden;
  margin-bottom: 10px; position: relative;

  @media (max-width: 640px) { margin-bottom: 6px; }
`;
const Image = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;     /* პროპორციულად ჩასვავს ჩარჩოში */
  object-position: center; /* სურვილისამებრ: ცენტრში გაასწორებს */
  display: block;          /* inline-gap აღარ იქნება */
  @media (max-width: 640px) { width: 80%; height: 80%; }
`;
const Title = styled.h2`
  font-size: 14px; margin: 4px 0 0; font-weight: 800; color: ${COLORS.text};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  @media (max-width: 640px) { font-size: 13px; }
`;
const PriceRow = styled.div`
  margin-top: 8px; font-weight: 800; color: ${COLORS.text}; font-size: 13px;
  .old { color: #9ca3af; text-decoration: line-through; margin-right: 6px; }
  @media (max-width: 640px) { font-size: 12px; }
`;

const CardLink = styled(Link)` text-decoration: none; color: inherit; display: flex; flex-direction: column; `;

export default function GiftSetsSlider({ addToCart }) {
    const { version } = useDataRefresh();
  const { language, t } = useLanguage();
  const [sets, setSets] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const trackRef = useRef(null);
  const sectionRef = useRef(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [qty, setQty] = useState(1);

  const gsKey = (s) => s.gift_set_id ?? String(s.id);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/gift_sets_public_list.php`, { params: { limit: 12, lang: language, language } })
      .then(({ data }) => setSets(Array.isArray(data) ? data : []))
      .catch(() => setSets([]));

    const onDocClick = (e) => { if (sectionRef.current && !sectionRef.current.contains(e.target)) setActiveCard(null); };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [language, version]);

  const syncArrows = () => {
    const el = trackRef.current; if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setAtStart(scrollLeft <= 2);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 2);
  };
  useEffect(() => {
    syncArrows();
    const el = trackRef.current; if (!el) return;
    const onScroll = () => syncArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", syncArrows);
    return () => { el.removeEventListener("scroll", onScroll); window.removeEventListener("resize", syncArrows); };
  }, []);

  const getStep = () => {
    const el = trackRef.current;
    if (!el || !el.firstElementChild) return 276;
    const first = el.firstElementChild.getBoundingClientRect();
    const second = el.children?.[1]?.getBoundingClientRect();
    const gap = second ? Math.max(8, Math.round(second.left - first.right)) : 16;
    return Math.round(first.width + gap);
  };
  const scrollLeft = () => { const el = trackRef.current; if (!el) return; el.scrollBy({ left: -getStep(), behavior: "smooth" }); };
  const scrollRight = () => { const el = trackRef.current; if (!el) return; el.scrollBy({ left: getStep(), behavior: "smooth" }); };

  const handleCardClick = (id, e) => { e.stopPropagation(); setActiveCard(prev => prev === id ? null : id); };

  // --- actions (ისევე, როგორც ქვედა სლაიდერში) ---
// --- actions (ისევე, როგორც ქვედა სლაიდერში) ---
// --- actions (ისევე, როგორც ქვედა სლაიდერში) ---
// GiftSetsSlider.jsx — replace addToCartQuick with this
const addToCartQuick = (s, e) => {
  e.stopPropagation();

  // ყოველთვის ვანიჭებთ პრიორიტეტს gift_set_id-ს
  const giftSetId = String(s.gift_set_id ?? s.id ?? s.slug);

  const title =
    language === "en"
      ? (s.title_en || s.title_ka || s.title)
      : (s.title_ka || s.title_en || s.title);

  const image =
    s.cover_url ||
    s.image_url ||
    (Array.isArray(s.images) && s.images[0]) ||
    "/placeholder-giftset.png";

  // full_stock ზუსტად გადავაცილოთ App-ში (თუ არაა -> 0, რომ ლიმიტი არ გაუთავდეს)
  const fullStock = Number(s.full_stock ?? 0);

  // App.addToCart-ს პირდაპირ ვაწოდებთ gift_set ფორმატით
  if (typeof addToCart === "function") {
    addToCart({
      type: "gift_set",
      product_type: "gift_set",

      id: giftSetId,
      product_id: giftSetId,
      gift_set_id: giftSetId,

      title,
      name: title,
      unit_price: Number(s.final_price || 0),

      quantity: 1, // სლაიდერიდან 1 ცალი
      full_stock: fullStock,

      image,       // რომ კალათაში და „sold“-ში სურათი იყოს
    });
    return;
  }

  // fallback (თუ addToCart prop არ გადმოეცა) — ვინახავთ იგივე იდენტიფიკაციით
  try {
    const key = "cart";
    const cart = JSON.parse(localStorage.getItem(key) || "[]");

    cart.push({
      type: "gift_set",
      product_type: "gift_set",

      id: giftSetId,
      product_id: giftSetId,
      gift_set_id: giftSetId,

      title,
      unit_price: Number(s.final_price || 0),

      quantity: 1,
      full_stock: fullStock,

      image,
      cover: image,
      // სურვილისამებრ: slug: s.slug
    });

    localStorage.setItem(key, JSON.stringify(cart));
    alert(language === "en" ? "Gift set added to cart" : "გიფტ სეტი დაემატა კალათაში");
  } catch {}
};

  return (
    <Section className="new-products" ref={sectionRef}>
      <h3>{t?.("giftsets.header") || "სასაჩუქრე ნაკრები"}</h3>

      <CarouselWrap>
        <ArrowLeft onClick={scrollLeft} disabled={atStart} aria-label="Scroll left">‹</ArrowLeft>

        <Track ref={trackRef}>
          {sets.map((set) => {
            const isActive = activeCard === set.id;
            const hasSaving = (set.compare_price || 0) > (set.final_price || 0);
            const percent = hasSaving
              ? Math.round(((set.compare_price - set.final_price) / set.compare_price) * 100)
              : 0;

            const cover = set.cover_url || set.image_url || "/placeholder-giftset.png";
            const key = gsKey(set);
            const inStock = (set.full_stock ?? 0) >= 1; // gift set-ზეც მუშაობს

            return (
              <Card key={set.id} onClick={(e) => handleCardClick(set.id, e)} $active={isActive}>
                {/* Overlay — მხოლოდ დაკლიკვაზე ჩანს; overlay-ზე ცარიელ ადგილას დაკლიკებით დაიხუროს */}
                <div
                  className="hover-options"
                  onClick={(e) => { e.stopPropagation(); setActiveCard(null); }}
                >
                  <Link
                    to={`/gift-set/${set.gift_set_id ?? set.slug ?? set.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="view-btn">{t?.("categorypage.viewmore") || "სრულად ნახვა"}</button>
                  </Link>
                </div>

                {/* ზუსტად იგივე top-left IconBar */}
                <IconBar>
                  {inStock && (
                  <button
                    className="icon-btn"
                    onClick={(e) => addToCartQuick(set, e)}
                    aria-label={t("categorypage.addtocart") || "Add to cart"}
                    title={t("categorypage.addtocart") || "Add to cart"}
                  >
                    <ShoppingCart size={18} />
                  </button>
                  )}
                  <div className="icon-btn" onClick={(e) => e.stopPropagation()}>
                    {/* FavoriteButton gift set-ზე */}
                  <FavoriteButton kind="gift_set" giftSetId={set.gift_set_id || set.slug} initialIsFavorite={set.is_favorite}/>
                  </div>
                </IconBar>

                {hasSaving && <SaleBadge>{percent}%</SaleBadge>}

                <CardLink>
                  <ImgWrap>
                    <Image src={cover} alt={set.title || set.title_en || set.title_ka} />
                  </ImgWrap>

                  <Title title={set.title || set.title_en || set.title_ka}>
                    {set.title || set.title_en || set.title_ka}
                  </Title>

                  <PriceRow>
                    {hasSaving && <span className="old">{Number(set.compare_price).toFixed(2)} ₾</span>}
                    <span>{Number(set.final_price || 0).toFixed(2)} ₾</span>
                  </PriceRow>
                </CardLink>
              </Card>
            );
          })}
        </Track>

        <ArrowRight onClick={scrollRight} disabled={atEnd} aria-label="Scroll right">›</ArrowRight>
      </CarouselWrap>
            <div className="all-perfumes-cont" style={{ textAlign: "center", marginTop: 12 }}>
              <Link
                to="/gift-sets"
                className="all-perfumes"
                style={{
                  display: "inline-block",
                  padding: "10px 16px",
                  borderRadius: 12,
                  background: COLORS.text,
                  color: "#fff",
                  textDecoration: "none",
                  fontWeight: 800,
                  border: `1px solid ${COLORS.line}`,
                  boxShadow: COLORS.shadow,
                }}
              >
                {t("newproducts.viewall")}
              </Link>
            </div>
      
    </Section>
  );
}
